/**
 * The dialogs that can be opened from more than one place — the command palette,
 * the assistant, a table row, a page header. Each owns its own form state and its
 * own mutation, so the pages that trigger them stay declarative.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useOverlay } from '../lib/overlay.jsx';
import { Icon } from '../lib/icons.jsx';
import { Field, ErrorNote, AiCard, Badge , Switch } from './ui.jsx';
import { todayISO, addDays, daysBetween, fmtDate } from '../lib/format.js';

const TICKET_CATEGORIES = ['Payroll', 'Attendance', 'Leave', 'Documents', 'IT Access', 'Benefits', 'Policy', 'Grievance', 'Facilities'];

/**
 * Categories that never reach a manager, whatever the switch says.
 *
 * Mirrors the same list on the server, which is the authority. Here it decides
 * whether to show the choice at all — a switch left on and then quietly ignored
 * would be worse than not offering it.
 */
const CONFIDENTIAL_CATEGORIES = ['Grievance', 'POSH', 'Harassment', 'Whistleblower'];

/* ------------------------------------------------------------- apply for leave */

function LeaveForm({ close }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { toast } = useOverlay();
  const [form, setForm] = useState({
    leaveTypeId: '',
    fromDate: addDays(todayISO(), 3),
    toDate: addDays(todayISO(), 4),
    reason: '',
    isHalfDay: false,
    contactDuringLeave: '',
  });

  const types = useQuery({ queryKey: ['leave-types'], queryFn: () => get('/leave/types'), staleTime: 300_000 });
  const balances = useQuery({ queryKey: ['leave', 'balances', user.id], queryFn: () => get(`/leave/balances/${user.id}`) });

  const apply = useMutation({
    mutationFn: (body) => post('/leave/requests', body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['leave'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
      close();
      const lop = res?.data?.lopDays;
      toast(lop > 0 ? `Leave submitted — ${lop} day(s) will be loss of pay` : 'Leave request submitted for approval', 'good');
    },
  });

  const days = Math.max(1, daysBetween(form.fromDate, form.toDate) + 1);
  const selected = (balances.data?.data ?? []).find((b) => b.leaveTypeId === form.leaveTypeId);
  const shortfall = selected ? days - selected.available : 0;

  return (
    <>
      {balances.data?.data?.length > 0 && (
        <div className="grid g-3" style={{ gap: '0 12px', marginBottom: 14 }}>
          {balances.data.data.map((b) => (
            <button
              key={b.id}
              type="button"
              className="card pointer"
              style={{
                padding: 11,
                boxShadow: 'none',
                textAlign: 'left',
                borderColor: form.leaveTypeId === b.leaveTypeId ? 'var(--brand-500)' : undefined,
              }}
              onClick={() => setForm((f) => ({ ...f, leaveTypeId: b.leaveTypeId }))}
            >
              <div style={{ fontWeight: 650, fontSize: 12.8 }}>{b.leaveType.name}</div>
              <div style={{ fontSize: 20, fontWeight: 750, letterSpacing: '-.03em', marginTop: 3 }}>
                {b.available}
                <span className="txt-sm muted" style={{ fontWeight: 600 }}> days left</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <form
        id="leave-form"
        onSubmit={(e) => { e.preventDefault(); apply.mutate(form); }}
      >
        <div className="grid g-2" style={{ gap: '0 14px' }}>
          <Field label="Leave type" htmlFor="lv-type">
            <select
              id="lv-type" className="inp" required value={form.leaveTypeId}
              onChange={(e) => setForm({ ...form, leaveTypeId: e.target.value })}
            >
              <option value="">Choose a type…</option>
              {(types.data?.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Duration" htmlFor="lv-half">
            <select
              id="lv-half" className="inp" value={form.isHalfDay ? 'half' : 'full'}
              onChange={(e) => setForm({ ...form, isHalfDay: e.target.value === 'half' })}
            >
              <option value="full">Full day</option>
              <option value="half">Half day</option>
            </select>
          </Field>
          <Field label="From" htmlFor="lv-from">
            <input id="lv-from" type="date" className="inp" required value={form.fromDate}
              onChange={(e) => setForm({ ...form, fromDate: e.target.value })} />
          </Field>
          <Field label="To" htmlFor="lv-to">
            <input id="lv-to" type="date" className="inp" required value={form.toDate}
              onChange={(e) => setForm({ ...form, toDate: e.target.value })} />
          </Field>
        </div>

        <Field label="Reason" htmlFor="lv-reason">
          <textarea
            id="lv-reason" className="inp" required minLength={3} value={form.reason}
            placeholder="A line is enough — your manager sees this"
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        </Field>

        <Field label="Contact while away" htmlFor="lv-contact">
          <input id="lv-contact" className="inp" value={form.contactDuringLeave}
            placeholder="Phone number" onChange={(e) => setForm({ ...form, contactDuringLeave: e.target.value })} />
        </Field>
      </form>

      <AiCard tag="Before you submit">
        <div className="txt-sm" style={{ color: 'var(--text-2)' }}>
          {form.fromDate && form.toDate && (
            <div style={{ marginBottom: 6 }}>
              {fmtDate(form.fromDate)} – {fmtDate(form.toDate)} is <b>{days} calendar day{days > 1 ? 's' : ''}</b>.
              {' '}The server applies the sandwich rule, so a weekly off between two leave days is charged too.
            </div>
          )}
          {selected && shortfall > 0 && (
            <div><Badge tone="crit">Short by {shortfall} day(s) — the excess becomes loss of pay</Badge></div>
          )}
          {selected && shortfall <= 0 && (
            <div><Badge tone="good">Within your {selected.leaveType.name} balance</Badge></div>
          )}
        </div>
      </AiCard>

      <ErrorNote error={apply.error} />
    </>
  );
}

/* ---------------------------------------------------------------- raise ticket */

function TicketForm({ close }) {
  const qc = useQueryClient();
  const { toast } = useOverlay();
  const [form, setForm] = useState({
    category: 'Payroll', subject: '', body: '', priority: 'MEDIUM', visibleToManager: true,
  });

  // Raising one of these is already the decision; the choice below is removed
  // rather than left on and quietly ignored by the server.
  const alwaysPrivate = CONFIDENTIAL_CATEGORIES.includes(form.category);
  const managerWillSee = form.visibleToManager && !alwaysPrivate;

  const raise = useMutation({
    mutationFn: (body) => post('/helpdesk/tickets', body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      close();
      toast(`Ticket ${res?.data?.reference ?? ''} raised — HR will respond within the SLA`, 'good');
    },
  });

  return (
    <>
      <form id="ticket-form" onSubmit={(e) => { e.preventDefault(); raise.mutate(form); }}>
        <Field label="Category" htmlFor="tk-cat">
          <select id="tk-cat" className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {TICKET_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Subject" htmlFor="tk-sub">
          <input id="tk-sub" className="inp" required minLength={3} value={form.subject}
            placeholder="One line describing what you need"
            onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </Field>
        <Field label="Details" htmlFor="tk-body">
          <textarea id="tk-body" className="inp" required minLength={3} value={form.body}
            placeholder="Dates, amounts, anything that helps HR resolve it in one pass"
            onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </Field>
        <Field label="Priority" htmlFor="tk-pri">
          <select id="tk-pri" className="inp" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => <option key={p} value={p}>{p[0] + p.slice(1).toLowerCase()}</option>)}
          </select>
        </Field>

        {alwaysPrivate ? (
          <div className="hint">
            <Icon name="lock" /> A {form.category.toLowerCase()} goes to HR and leadership only. Your manager is not
            told about it and cannot see it.
          </div>
        ) : (
          <div className="flex items-c gap-10" style={{ padding: '9px 0', borderTop: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.2 }}>Let my manager see this</div>
              <div className="txt-sm muted">
                {managerWillSee
                  ? 'They will be told you raised it, and can follow it.'
                  : 'This goes to HR and leadership only. Your manager is not told and cannot see it.'}
              </div>
            </div>
            <Switch on={form.visibleToManager} onChange={(v) => setForm({ ...form, visibleToManager: v })} />
          </div>
        )}
      </form>
      <ErrorNote error={raise.error} />
    </>
  );
}

/* ------------------------------------------------------------ regularise a day */

function RegulariseForm({ close, date }) {
  const qc = useQueryClient();
  const { toast } = useOverlay();
  const [form, setForm] = useState({
    date: date ?? addDays(todayISO(), -1),
    issue: 'Missing out-punch',
    claimedInAt: '09:30',
    claimedOutAt: '18:30',
    reason: '',
  });

  const submit = useMutation({
    mutationFn: (body) => post('/attendance/regularisations', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.invalidateQueries({ queryKey: ['regularisations'] });
      close();
      toast('Regularisation request sent for approval', 'good');
    },
  });

  return (
    <>
      <form id="reg-form" onSubmit={(e) => { e.preventDefault(); submit.mutate(form); }}>
        <Field label="Date" htmlFor="rg-date">
          <input id="rg-date" type="date" className="inp" required max={todayISO()} value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <Field label="What happened" htmlFor="rg-issue">
          <select id="rg-issue" className="inp" value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })}>
            {['Missing in-punch', 'Missing out-punch', 'Biometric not captured', 'Device offline', 'Client visit — no punch', 'Work from home — VPN issue']
              .map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <div className="grid g-2" style={{ gap: '0 14px' }}>
          <Field label="Actual in time" htmlFor="rg-in">
            <input id="rg-in" type="time" className="inp" value={form.claimedInAt}
              onChange={(e) => setForm({ ...form, claimedInAt: e.target.value })} />
          </Field>
          <Field label="Actual out time" htmlFor="rg-out">
            <input id="rg-out" type="time" className="inp" value={form.claimedOutAt}
              onChange={(e) => setForm({ ...form, claimedOutAt: e.target.value })} />
          </Field>
        </div>
        <Field label="Note for your manager" htmlFor="rg-note">
          <textarea id="rg-note" className="inp" required minLength={3} value={form.reason}
            placeholder="Anything that helps them verify it"
            onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </form>
      <div className="txt-sm muted"><Icon name="info" /> The raw punch log is never edited — a regularisation
        records the correction alongside it, which is what makes a disputed day defensible later.</div>
      <ErrorNote error={submit.error} />
    </>
  );
}

/* --------------------------------------------------- ask for different hours */

function ShiftChangeForm({ close }) {
  const qc = useQueryClient();
  const { toast } = useOverlay();
  const shifts = useQuery({ queryKey: ['shifts'], queryFn: () => get('/reference/shifts'), staleTime: 300_000 });
  const [form, setForm] = useState({
    fromDate: addDays(todayISO(), 1),
    toDate: '',
    mode: 'times',
    shiftId: '',
    startTime: '08:00',
    endTime: '17:00',
    reason: '',
  });
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  const submit = useMutation({
    mutationFn: (body) => post('/attendance/shift-changes', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-changes'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
      close();
      toast('Sent to your manager for approval', 'good');
    },
  });

  const send = () => submit.mutate({
    fromDate: form.fromDate,
    // Blank means the one day. Sending the same date twice would work too, but
    // an absent field says "just that day" more plainly in the audit trail.
    ...(form.toDate ? { toDate: form.toDate } : {}),
    ...(form.mode === 'shift' ? { shiftId: form.shiftId } : { startTime: form.startTime, endTime: form.endTime }),
    reason: form.reason,
  });

  const ready = form.reason.trim().length >= 5 && (form.mode === 'times' || form.shiftId);

  return (
    <>
      <form id="shift-form" onSubmit={(e) => { e.preventDefault(); if (ready) send(); }}>
        <div className="grid g-2" style={{ gap: '0 14px' }}>
          <Field label="From" htmlFor="sc-from">
            <input id="sc-from" type="date" className="inp" required min={todayISO()} value={form.fromDate}
              onChange={(e) => set({ fromDate: e.target.value })} />
          </Field>
          <Field label="To" hint="Leave blank for a single day." htmlFor="sc-to">
            <input id="sc-to" type="date" className="inp" min={form.fromDate} value={form.toDate}
              onChange={(e) => set({ toDate: e.target.value })} />
          </Field>
        </div>

        <Field label="Hours">
          <div className="flex gap-6" style={{ marginBottom: 10 }}>
            <button type="button" className={`chip${form.mode === 'times' ? ' on' : ''}`} onClick={() => set({ mode: 'times' })}>
              Give the times
            </button>
            <button type="button" className={`chip${form.mode === 'shift' ? ' on' : ''}`} onClick={() => set({ mode: 'shift' })}>
              Use an existing shift
            </button>
          </div>

          {form.mode === 'times' ? (
            <div className="grid g-2" style={{ gap: '0 14px' }}>
              <input id="sc-start" type="time" className="inp" required value={form.startTime}
                onChange={(e) => set({ startTime: e.target.value })} />
              <input id="sc-end" type="time" className="inp" required value={form.endTime}
                onChange={(e) => set({ endTime: e.target.value })} />
            </div>
          ) : (
            <select className="inp" value={form.shiftId} onChange={(e) => set({ shiftId: e.target.value })}>
              <option value="">Choose a shift…</option>
              {(shifts.data?.data ?? []).map((sh) => (
                <option key={sh.id} value={sh.id}>{sh.name} ({sh.startTime}–{sh.endTime})</option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Why" htmlFor="sc-why">
          <textarea id="sc-why" className="inp" required minLength={5} value={form.reason}
            placeholder="Hospital appointment at four; I would start and finish earlier."
            onChange={(e) => set({ reason: e.target.value })} />
        </Field>
      </form>
      <div className="txt-sm muted">
        <Icon name="info" /> Your usual shift does not change. Only these days are measured against the new
        hours, and only once your manager approves — until then you are expected at your normal time.
      </div>
      <ErrorNote error={submit.error} />
    </>
  );
}

/* ------------------------------------------------------------------- the hook */

export function useActions() {
  const { openModal, closeModal } = useOverlay();

  const submitFooter = (formId, label) => ({ close }) => (
    <>
      <button type="button" className="btn" onClick={close}>Cancel</button>
      <button type="submit" form={formId} className="btn btn-primary"><Icon name="send" />{label}</button>
    </>
  );

  return {
    applyLeave: () => openModal({
      title: 'Apply for leave',
      subtitle: 'Goes to your reporting manager for approval',
      body: ({ close }) => <LeaveForm close={close} />,
      footer: submitFooter('leave-form', 'Submit request'),
    }),

    raiseTicket: () => openModal({
      title: 'Raise a request',
      subtitle: 'HR, IT and Finance requests all start here',
      body: ({ close }) => <TicketForm close={close} />,
      footer: submitFooter('ticket-form', 'Submit'),
    }),

    regularise: (date) => openModal({
      title: 'Regularise attendance',
      subtitle: 'For a missed punch, device failure or off-site work',
      body: ({ close }) => <RegulariseForm close={close} date={date} />,
      footer: submitFooter('reg-form', 'Submit'),
    }),

    requestShiftChange: () => openModal({
      title: 'Ask to change your hours',
      subtitle: 'For a day or two — an appointment, an event, something at home',
      body: ({ close }) => <ShiftChangeForm close={close} />,
      footer: submitFooter('shift-form', 'Send to my manager'),
    }),

    close: closeModal,
  };
}
